/**
 * /admin/banner — Controle do banner rotativo da home.
 *
 * Controle manual manda em tudo: ativa/desativa e ordena todos os slides,
 * inclusive os das ações (Plantão FEO, Flash FEO, Semana da Fisio).
 */
import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { ArrowDown, ArrowUp, Images, Monitor, RotateCcw, Save, Smartphone } from "lucide-react";
import { toast } from "sonner";

import { AdminGate } from "@/components/admin/AdminGate";
import { AdminShell, AdminSection, AdminCard } from "@/components/admin/shell";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { SLIDE_META, SLIDE_REGISTRY } from "@/components/home/banner-slides";
import { DEFAULT_BANNER_CONFIG, type BannerConfig } from "@/lib/home/banner";
import { getBannerConfigFn, setBannerConfigFn } from "@/lib/home/banner.functions";
import { getAcoesStateFn } from "@/lib/campanhas/acoes.functions";

/** Preview fiel do slide: iframe na largura do dispositivo, reduzido para caber. */
const DEVICES = {
  desktop: { width: 1280, height: 640, label: "Desktop" },
  mobile: { width: 390, height: 500, label: "Mobile" },
} as const;
type DeviceKey = keyof typeof DEVICES;

function SlidePreview({ slideKey }: { slideKey: string }) {
  const [device, setDevice] = useState<DeviceKey>("desktop");
  const wrapRef = useRef<HTMLDivElement>(null);
  const [available, setAvailable] = useState(0);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => setAvailable(entry.contentRect.width));
    ro.observe(el);
    setAvailable(el.clientWidth);
    return () => ro.disconnect();
  }, []);

  const dev = DEVICES[device];
  const scale = available > 0 ? Math.min(1, available / dev.width) : 1;

  return (
    <div className="w-full">
      <div className="mb-3 flex items-center gap-2">
        {(Object.keys(DEVICES) as DeviceKey[]).map((k) => {
          const Icon = k === "desktop" ? Monitor : Smartphone;
          return (
            <Button
              key={k}
              size="sm"
              variant={device === k ? "default" : "outline"}
              onClick={() => setDevice(k)}
            >
              <Icon className="mr-2 h-4 w-4" />
              {DEVICES[k].label}
            </Button>
          );
        })}
        <span className="ml-auto text-xs text-white/45">
          {dev.width}px · {Math.round(scale * 100)}%
        </span>
      </div>

      <div ref={wrapRef} className="w-full overflow-hidden rounded-lg border border-white/10 bg-black/40">
        <div style={{ height: dev.height * scale }} className="relative">
          <iframe
            key={`${slideKey}-${device}`}
            src={`/embed/banner-slide/${slideKey}`}
            title={`Preview ${device} do slide ${slideKey}`}
            loading="lazy"
            sandbox="allow-scripts allow-same-origin"
            className="pointer-events-none origin-top-left border-0"
            style={{ width: dev.width, height: dev.height, transform: `scale(${scale})` }}
          />
        </div>
      </div>
    </div>
  );
}


export const Route = createFileRoute("/admin/banner")({
  head: () => ({
    meta: [
      { title: "Admin — Banner da Home" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: () => (
    <AdminGate route="/admin/banner">
      <BannerAdminPage />
    </AdminGate>
  ),
});

function BannerAdminPage() {
  const loadConfig = useServerFn(getBannerConfigFn);
  const loadAcoes = useServerFn(getAcoesStateFn);
  const save = useServerFn(setBannerConfigFn);

  const configQuery = useQuery({ queryKey: ["admin", "banner-config"], queryFn: () => loadConfig() });
  const acoesQuery = useQuery({ queryKey: ["admin", "acoes-state"], queryFn: () => loadAcoes() });

  const [draft, setDraft] = useState<BannerConfig | null>(null);
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const togglePreview = (key: string) => setOpen((p) => ({ ...p, [key]: !p[key] }));


  useEffect(() => {
    if (configQuery.data) setDraft(configQuery.data);
  }, [configQuery.data]);

  const move = (index: number, dir: -1 | 1) => {
    setDraft((prev) => {
      if (!prev) return prev;
      const order = [...prev.order];
      const target = index + dir;
      if (target < 0 || target >= order.length) return prev;
      [order[index], order[target]] = [order[target], order[index]];
      return { ...prev, order };
    });
  };

  const toggle = (key: string, value: boolean) => {
    setDraft((prev) => (prev ? { ...prev, enabled: { ...prev.enabled, [key]: value } } : prev));
  };

  const handleSave = async () => {
    if (!draft) return;
    setSaving(true);
    try {
      await save({ data: draft });
      toast.success("Banner da home atualizado");
      await configQuery.refetch();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Falha ao salvar");
    } finally {
      setSaving(false);
    }
  };

  const ativos = draft ? draft.order.filter((k) => draft.enabled[k]).length : 0;

  return (
    <AdminShell
      title="BANNER DA HOME"
      description="Ativar, desativar e ordenar os slides do banner rotativo da homepage."
      icon={<Images className="h-5 w-5 text-admin-accent" />}
      actions={
        <>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setDraft({ ...DEFAULT_BANNER_CONFIG, enabled: { ...DEFAULT_BANNER_CONFIG.enabled } })}
          >
            <RotateCcw className="mr-2 h-4 w-4" /> Restaurar padrão
          </Button>
          <Button size="sm" onClick={handleSave} disabled={!draft || saving}>
            <Save className="mr-2 h-4 w-4" /> {saving ? "Salvando..." : "Salvar"}
          </Button>
        </>
      }
    >
      <AdminSection label={`Slides — ${ativos} no ar`}>
        {!draft ? (
          <AdminCard>
            <p className="text-sm text-white/55">Carregando configuração...</p>
          </AdminCard>
        ) : (
          <div className="space-y-3">
            {draft.order.map((key, index) => {
              const meta = SLIDE_META.find((m) => m.key === key);
              const slide = SLIDE_REGISTRY[key];
              if (!meta || !slide) return null;
              const enabled = draft.enabled[key] === true;
              const acaoAtiva = meta.acaoSlug
                ? acoesQuery.data?.[meta.acaoSlug as "plantaofeo" | "flashfeo" | "semanadafisio"]?.ativa === true
                : null;

              return (
                <AdminCard key={key} className="space-y-4">
                  <div className="flex flex-wrap items-center gap-4">
                  <div className="flex flex-col gap-1">
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-7 w-7"
                      disabled={index === 0}
                      onClick={() => move(index, -1)}
                      aria-label={`Subir ${meta.nome}`}
                    >
                      <ArrowUp className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-7 w-7"
                      disabled={index === draft.order.length - 1}
                      onClick={() => move(index, 1)}
                      aria-label={`Descer ${meta.nome}`}
                    >
                      <ArrowDown className="h-3.5 w-3.5" />
                    </Button>
                  </div>

                  {/* Prévia — cor de fundo, logo e cor de destaque do slide */}
                  <div
                    className="relative flex h-16 w-28 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-white/10"
                    style={{ background: slide.bgStyle ?? undefined }}
                  >
                    <span aria-hidden className={slide.bgStyle ? "" : `absolute inset-0 ${slide.bgClass}`} />
                    {slide.logo ? (
                      <img src={slide.logo} alt="" className="relative max-h-8 max-w-[80%] object-contain" />
                    ) : (
                      <span
                        className="relative text-[10px] font-bold uppercase tracking-widest"
                        style={{ color: slide.accent }}
                      >
                        {meta.key}
                      </span>
                    )}
                  </div>

                  <div className="min-w-0 flex-1">
                    <p className="flex flex-wrap items-center gap-2 text-sm font-bold text-white">
                      <span className="text-white/40">{index + 1}.</span>
                      {meta.nome}
                      {meta.acaoSlug && (
                        <Badge variant="outline" className="border-white/15 text-[10px] text-white/60">
                          AÇÃO {acaoAtiva ? "ATIVA" : "INATIVA"} em /admin/campanhas
                        </Badge>
                      )}
                    </p>
                    <p className="mt-0.5 truncate text-xs text-white/55">
                      {meta.desc} · {meta.rota}
                    </p>
                  </div>

                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={() => togglePreview(key)}>
                      {open[key] ? "Ocultar preview" : "Ver preview"}
                    </Button>
                    <span className="text-xs font-semibold text-white/55">{enabled ? "No ar" : "Fora do ar"}</span>
                    <Switch checked={enabled} onCheckedChange={(v) => toggle(key, v)} aria-label={`Ativar ${meta.nome}`} />
                  </div>
                  </div>

                  {open[key] && <SlidePreview slideKey={key} />}
                </AdminCard>
              );

            })}
          </div>
        )}
      </AdminSection>
    </AdminShell>
  );
}
